// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.
import type BaseBackendService from '../BaseBackendService';
import Contact from '../../models/Contact';
export const adminBackendServiceContactMixin = {
  searchContact(searchTerm: string, excludeCustodian: boolean = false): Promise<Contact[]> {
    const query = excludeCustodian
      ? `contacts/search?term=${encodeURIComponent(searchTerm)}&excludeCustodian=true`
      : `contacts/search?term=${encodeURIComponent(searchTerm)}`;
    return this.authApiGetAuthed(
      query,
      'Network error while searching contacts',
      (data) => data.data as Contact[],
    );
  },

  getContact(contactId: string): Promise<Contact> {
    return this.authApiGetAuthed(
      `contacts/${contactId}`,
      'Network error while fetching contact',
      (data) => data.data as Contact,
    );
  },

  startImpersonation(
    contact: Contact,
    options: { captureLogs?: boolean } = {},
  ): Promise<{ redirectUrl: string; mobileRedirectUrl?: string }> {
    return this.authApiMutateAuthed(
      `contacts/${contact.id}/impersonations/start`,
      'POST',
      { captureLogs: options.captureLogs === true },
      (json) => json as { redirectUrl: string; mobileRedirectUrl?: string },
    );
  },

  startImpersonationApp(
    contact: Contact,
    options: { captureLogs?: boolean } = {},
  ): Promise<{ handoffToken: string; expiresAt: string }> {
    return this.impersonationAppRequest(`contacts/${contact.id}/impersonations/start-app`, {
      captureLogs: options.captureLogs === true,
    }) as Promise<{ handoffToken: string; expiresAt: string }>;
  },

  redeemImpersonationAppHandoff(handoffToken: string): Promise<{ mobileRedirectUrl: string }> {
    return this.impersonationAppRequest(`impersonations/app-handoff/redeem`, {
      handoffToken,
    }) as Promise<{ mobileRedirectUrl: string }>;
  },

  // Bespoke POST that preserves the backend `{ error, code }` on failure so the
  // handoff landing page can distinguish HANDOFF_ACTOR_MISMATCH (wrong admin)
  // from INVALID_OR_USED_HANDOFF (expired / already used). authApiMutateAuthed
  // routes unknown codes through parseStructuredError, which discards them.
  impersonationAppRequest(path: string, body: unknown): Promise<unknown> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL(path), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        const err = new Error(
          (json as { error?: string })?.error || `Error: ${response.status}`,
        ) as Error & { code?: string; status: number };
        err.code = (json as { code?: string })?.code;
        err.status = response.status;
        throw err;
      }
      return json;
    });
  },

  createInvestor(contactId: string, fundLeadOpportunityId: string): Promise<string> {
    return this.authApiMutateAuthed(
      `fundOpportunities/${fundLeadOpportunityId}/investorCandidates`,
      'POST',
      { contactId },
      (data) => data.data.id as string,
    );
  },

  addAllCandidates(fundOpportunityId: string): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `fundOpportunities/${fundOpportunityId}/addAllCandidates`,
      'POST',
      undefined,
      () => undefined,
    );
  },

  createContact(contact: {
    firstName?: string;
    lastName?: string;
    email?: string;
    mobile?: string;
    dateOfBirth?: string;
    city?: string;
    country?: string;
    salutation?: string;
    custodian?: boolean;
  }): Promise<string> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL('contacts'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(contact),
      });
      if (!response.ok) {
        await new Promise<void>((_resolve, reject) => {
          this.handleErrorResponse(response, reject);
        });
      }
      const data = await response.json();
      if (data.error) {
        throw data.error;
      }
      return data.data.id as string;
    });
  },

  updateContact(
    contactId: string,
    contact: {
      firstName?: string;
      lastName?: string;
      email?: string;
      mobile?: string;
      dateOfBirth?: string;
      city?: string;
      country?: string;
      salutation?: string;
      custodian: boolean;
    },
  ): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `contacts/${contactId}`,
      'PATCH',
      contact,
      () => undefined,
    );
  },

  async getCustodianStatus(): Promise<{
    isCustodian: boolean;
    contactId: string;
    timestamp: string;
  }> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL('contacts/custodian-status'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw this.handleError(await response.json());
      }
      const json = await response.json();
      const { data, signature, publicKeyBase64 } = json;
      if (publicKeyBase64 && publicKeyBase64.trim() !== '') {
        if (!(await this.verifyConfig(data, signature, publicKeyBase64))) {
          throw this.handleError({
            code: 'SIGNATURE_VERIFICATION_FAILED',
            error: 'Invalid custodian status signature',
          });
        }
      }
      return data;
    }).catch((error) => {
      if (error?.code && error?.error) {
        throw error;
      }
      throw this.handleError({
        code: 'NETWORK_ERROR',
        error: 'Network error while fetching custodian status',
      });
    });
  },

  generateInvitationToken(contactId: string): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `contacts/${contactId}/invitation`,
      'POST',
      undefined,
      () => undefined,
    );
  },

  regenerateInvitationToken(contactId: string): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `contacts/${contactId}/invitation/regenerate`,
      'POST',
      undefined,
      () => undefined,
    );
  },

  getInvitationEmailTemplate(contactId: string): Promise<{
    html: string;
    subject: string;
    gmailComposeUrl: string;
    contact: {
      email: string;
      firstName: string;
      lastName: string;
    };
    invitationLink: string;
    expiryDays: number;
  }> {
    return this.authApiGetAuthed(
      `contacts/${contactId}/invitation/email-template`,
      'Network error while fetching invitation email template',
      (json) => json.data,
    );
  },

  sendInvitationEmail(
    contactId: string,
    options?: {
      customHtml?: string;
      subject?: string;
      replyTo?: string;
      from?: string;
      sendAsMe?: boolean;
      reset?: boolean;
    },
  ): Promise<{ messageId: string; success: boolean }> {
    return this.authApiMutateAuthed(
      `contacts/${contactId}/invitation/send-email`,
      'POST',
      options || {},
      (json) => json.data,
    );
  },

  generateAdminInvitationToken(adminUserId: string): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `adminUsers/${adminUserId}/invitation`,
      'POST',
      undefined,
      () => undefined,
    );
  },

  regenerateAdminInvitationToken(adminUserId: string): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `adminUsers/${adminUserId}/invitation/regenerate`,
      'POST',
      undefined,
      () => undefined,
    );
  },

  validateAdminInvitationToken(
    invitationToken: string,
  ): Promise<{ valid: boolean; firstName: string }> {
    return this.authenticatedFetch(
      this.apiURL(`adminUsers/invitation/validate/${invitationToken}`),
    ).then(async (response) => {
      const data = (await response.json()) as { valid: boolean; firstName: string };
      if (response.status === 410) {
        throw 'EXPIRED';
      }
      if (![200, 304].includes(response.status)) {
        throw (data as any).error?.message;
      }
      return data;
    });
  },

  submitIRRUpdateCsv(fundOpportunityId: string, formData: FormData): Promise<{ id: string }> {
    return this.authFetchPromise(async (token) => {
      const file = formData.get('file') as File | null;
      const month = formData.get('month') as string | null;

      if (!file || !month) {
        throw new Error('file and month are required');
      }

      const res = await this.authenticatedFetch(
        this.apiURL(`fundOpportunities/${fundOpportunityId}/irrUploadUrl`),
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ fileName: file.name, month }),
        },
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw errBody || new Error('Failed to get IRR upload URL');
      }
      const data = await res.json();
      const { uploadUrl, gcsPath } = data?.data || {};
      if (!uploadUrl || !gcsPath) {
        throw new Error('Invalid response from irrUploadUrl');
      }

      const ext = file.name.toLowerCase();
      const contentType =
        file.type ||
        (ext.endsWith('.csv')
          ? 'text/csv'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

      await this.putFileViaXhr(uploadUrl, file, contentType);

      const importRes = await fetch(
        this.apiURL(`fundOpportunities/${fundOpportunityId}/irrImportFromGcs`),
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ gcsPath, month, fileName: file.name }),
        },
      );

      if (!importRes.ok) {
        const errBody = await importRes.json().catch(() => ({}));
        throw errBody || new Error('Failed to import IRR data from storage');
      }

      return importRes.json();
    });
  },

  deleteIRRUpdate(id: string, fundOpportunityId: string): Promise<void> {
    return this.authApiDeleteVoid(`fundOpportunities/${fundOpportunityId}/irrUpdates/${id}`);
  },
} satisfies ThisType<BaseBackendService>;
